import React, { useCallback, useRef, useState } from "react"
import { ReactComponent as UploadIcon } from "@ciq/shared/assets/icons/button/upload_icon.svg"
import { ReactComponent as ClearIcon } from "@ciq/shared/assets/icons/button/clear_icon.svg"
import { EntryModal } from "@ciq/shared/components/modal"
import { useFileList } from "../hooks/useFileList"
import { PLACEMENT_STATUS } from "@ciq/shared/pages/file_upload/constants"
import { getValidationError } from "@ciq/shared/pages/file_upload/constants"
import { formatFileSize } from "@ciq/shared/pages/file_upload/utils"
import { FileListHeader } from "@ciq/shared/pages/file_upload/components/FileListHeader"
import { FileRow } from "@ciq/shared/pages/file_upload/components/FileRow"
import { BulkActionBar } from "./BulkActionBar"
import { FileDropZone } from "./FileDropZone"

/**
 * @typedef {Object} FloorPlansTabProps
 * @property {boolean} show - Whether this tab is visible
 * @property {Array} files - Array of uploaded file objects from API (already filtered by sidebar selection)
 * @property {string} schoolId - Current school ID for API calls
 * @property {Function} onFilesChanged - Callback to trigger parent file re-fetch
 * @property {boolean} [loading] - Boolean indicating if files are being loaded
 * @property {(files: File[]) => void} onFilesSelect - Callback when files are selected for upload
 * @property {(error: string) => void} onError - Callback when file validation fails
 * @property {Array} uploads - Current upload progress items
 * @property {(id: string) => void} onRetry - Callback to retry failed upload
 * @property {(id: string) => void} onDismiss - Callback to dismiss failed upload
 * @property {boolean} isInbox - Whether the selected item is the Inbox
 * @property {boolean} [moveEnabled=false] - Whether file move features are enabled (feature flag)
 * @property {Array} [buildings=[]] - Building items for MoveToDropdown
 */

/**
 * Floor Plans tab content for the File Upload page.
 * Renders file list with selection, status management, bulk actions, and upload support.
 * Shows drag-and-drop zone for empty state, collapsed upload button when files exist.
 *
 * @param {FloorPlansTabProps} props
 * @returns {React.ReactElement|null} The floor plans tab or null if hidden
 */
export function FloorPlansTab({
  show,
  files,
  schoolId,
  onFilesChanged,
  loading,
  onFilesSelect,
  onError,
  uploads,
  onRetry,
  onDismiss,
  isInbox,
  moveEnabled = false,
  buildings = [],
}) {
  if (!show) return null

  if (loading) return null

  const hasFiles = files && files.length > 0
  const hasUploads = uploads && uploads.length > 0

  // Empty state: show full drop zone
  if (!hasFiles && !hasUploads) {
    return (
      <div className="file-upload-floor-plans">
        <FileDropZone
          onFilesSelect={onFilesSelect}
          onError={onError}
        />
      </div>
    )
  }

  // Files exist: show header + file list + upload support
  return (
    <FloorPlansContent
      files={files}
      schoolId={schoolId}
      onFilesChanged={onFilesChanged}
      onFilesSelect={onFilesSelect}
      onError={onError}
      uploads={uploads}
      onRetry={onRetry}
      onDismiss={onDismiss}
      isInbox={isInbox}
      moveEnabled={moveEnabled}
      buildings={buildings}
    />
  )
}

/**
 * Inner content component (separated to allow hooks -- hooks cannot be called after early returns).
 * Combines PR #748's file list with selection/status/bulk actions and ENG-4917's upload support.
 *
 * @param {Object} props
 * @param {Array} props.files - Array of uploaded file objects
 * @param {string} props.schoolId - Current school ID
 * @param {Function} props.onFilesChanged - Callback to trigger parent re-fetch
 * @param {(files: File[]) => void} props.onFilesSelect - Callback when files are selected for upload
 * @param {(error: string) => void} props.onError - Callback when file validation fails
 * @param {Array} props.uploads - Current upload progress items
 * @param {(id: string) => void} props.onRetry - Callback to retry failed upload
 * @param {(id: string) => void} props.onDismiss - Callback to dismiss failed upload
 * @param {boolean} props.isInbox - Whether the selected item is the Inbox
 * @param {boolean} [props.moveEnabled=false] - Whether file move features are enabled
 * @param {Array} [props.buildings=[]] - Building items for MoveToDropdown
 * @returns {React.ReactElement} The file list content with upload support
 */
function FloorPlansContent({
  files,
  schoolId,
  onFilesChanged,
  onFilesSelect,
  onError,
  uploads,
  onRetry,
  onDismiss,
  isInbox,
  moveEnabled = false,
  buildings = [],
}) {
  const {
    selectedIds,
    allSelected,
    statusFilter,
    filteredFiles,
    deleteConfirmOpen,
    toggleSelect,
    selectAll,
    setStatusFilter,
    updateStatus,
    bulkUpdateStatus,
    bulkMoveToBuilding,
    requestBulkDelete,
    confirmBulkDelete,
    cancelBulkDelete,
    download,
  } = useFileList(files, schoolId, onFilesChanged)

  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)

  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current++
    if (e.dataTransfer.items?.length > 0) setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    dragCounterRef.current = 0
    const droppedFiles = e.dataTransfer.files
    if (!droppedFiles || droppedFiles.length === 0) return
    const validFiles = []
    for (const file of Array.from(droppedFiles)) {
      const error = getValidationError(file)
      if (error) {
        onError?.(error)
        continue
      }
      validFiles.push(file)
    }
    if (validFiles.length > 0) onFilesSelect(validFiles)
  }, [onFilesSelect, onError])

  const hasUploads = uploads && uploads.length > 0

  return (
    <div
      className="file-upload-floor-plans"
      style={{ position: "relative" }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="file-upload-drag-overlay">
          <UploadIcon />
          <span>Drop files here</span>
        </div>
      )}

      {hasUploads && (
        <div>
          {uploads.map(upload => (
            <UploadingFileRow
              key={upload.id}
              upload={upload}
              onRetry={onRetry}
              onDismiss={onDismiss}
            />
          ))}
        </div>
      )}

      <div>
        <div className="file-list-header-row">
          <FileListHeader
            fileCount={filteredFiles.length}
            selectedCount={selectedIds.size}
            allSelected={allSelected}
            onSelectAll={selectAll}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
          <BulkActionBar
            selectedCount={selectedIds.size}
            onMarkPlaced={() => bulkUpdateStatus(PLACEMENT_STATUS.PLACED)}
            onMarkOutOfScope={() => bulkUpdateStatus(PLACEMENT_STATUS.OUT_OF_SCOPE)}
            onDelete={requestBulkDelete}
            moveEnabled={moveEnabled}
            buildings={buildings}
            onMoveToBuilding={bulkMoveToBuilding}
          />
        </div>
        {filteredFiles.map(file => (
          <FileRow
            key={file.id}
            file={file}
            isSelected={selectedIds.has(file.id)}
            onSelect={toggleSelect}
            onStatusChange={updateStatus}
            onDownload={download}
            dragEnabled={moveEnabled}
            selectedFileIds={selectedIds}
          />
        ))}
      </div>

      <EntryModal
        show={deleteConfirmOpen}
        title="Delete Files"
        onHide={cancelBulkDelete}
        buttons={{
          secondary: { title: "Cancel", onClick: cancelBulkDelete },
          danger: { title: "Delete", onClick: confirmBulkDelete },
        }}
      >
        <p>
          Are you sure you want to delete {selectedIds.size} file{selectedIds.size !== 1 ? "s" : ""}?
          This cannot be undone.
        </p>
      </EntryModal>
    </div>
  )
}

/**
 * File row for an in-progress upload. Shows filename, size, and a circular spinner.
 * Error uploads show retry + dismiss actions.
 *
 * @param {Object} props
 * @param {Object} props.upload - Upload item from useFileUpload
 * @param {(id: string) => void} props.onRetry - Retry callback
 * @param {(id: string) => void} props.onDismiss - Dismiss callback
 * @returns {React.ReactElement}
 */
function UploadingFileRow({ upload, onRetry, onDismiss }) {
  const isError = upload.status === "error"

  return (
    <div className={`file-row--uploading ${isError ? "file-row--error" : ""}`}>
      <div className="file-row--uploading__main">
        <div className="file-row--uploading__left">
          <span className="file-row--uploading__name">{upload.filename}</span>
          <span className="file-row--uploading__size">
            {formatFileSize(upload.file?.size)}
          </span>
        </div>
        <div className="file-row--uploading__right">
          {isError ? (
            <>
              <button
                type="button"
                className="file-row--uploading__icon-btn"
                onClick={() => onRetry(upload.id)}
                aria-label="Retry upload"
                title="Retry"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M1.5 7.5C1.5 7.5 3.16 4.95 4.63 3.47C6.1 2 8.18 1.5 9 1.5C13.14 1.5 16.5 4.86 16.5 9C16.5 13.14 13.14 16.5 9 16.5C5.53 16.5 2.61 14.08 1.74 10.85" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M1.5 3V7.5H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button
                type="button"
                className="file-row--uploading__icon-btn file-row--uploading__icon-btn--danger"
                onClick={() => onDismiss(upload.id)}
                aria-label="Dismiss"
                title="Dismiss"
              >
                <ClearIcon />
              </button>
            </>
          ) : (
            <ProgressSpinner progress={upload.progress} />
          )}
        </div>
      </div>
      {isError && upload.error && (
        <span className="file-row--uploading__error-message">{upload.error}</span>
      )}
    </div>
  )
}

/**
 * Small circular SVG spinner for upload progress.
 *
 * @param {Object} props
 * @param {number} props.progress - Upload progress percentage (0-100)
 * @returns {React.ReactElement}
 */
function ProgressSpinner({ progress }) {
  const size = 20
  const strokeWidth = 2.5
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (progress / 100) * circumference

  return (
    <svg
      className="file-row--uploading__spinner"
      role="img"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={`Uploading ${progress}%`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--neutral-color-200)"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--primary-color-500)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  )
}
